// @ts-check

/**
 * Customer active coupons refresh.
 *
 * Logged-in only. When the shopper opens their profile or the cart drawer, this
 * module GETs the Hydrogen App Proxy route which:
 *   1. Calls Boomi `/SHPY/Customer/{mf08}/ActiveCoupons` server-side (so the
 *      Boomi API key never reaches the browser).
 *   2. Writes the raw payload to the customer metafield
 *      `custom.mf17_available_discounts` (json).
 *   3. Reconciles each coupon into a per-customer Shopify code discount
 *      (creates active ones, deactivates used/expired ones).
 *
 * UI: the coupon cards are enriched server-side (the mo02 metaobject joined
 * against MF17 in `snippets/cart-available-discounts.liquid`), so they can't be
 * rebuilt from the JSON response alone. After the fetch writes MF17 we re-fetch
 * the current page and swap the freshly-rendered `[data-cart-available-discounts]`
 * container(s) in place — skeleton loaders stay up until the swap lands. The
 * whole flow is best-effort and fail-silent; a failure must never break the page
 * or the cart drawer (we just leave the existing SSR markup in place).
 *
 * Identity / brand routing mirrors `cart-hydrogen-sync.js` and the loyalty
 * card: MF08 + the brand keyword are read from the `<html>` element
 * (`data-customer-mf08`, `data-hydrogen-api-brand`), both stamped by
 * `layout/theme.liquid`. Without the brand keyword COBS requests resolve
 * against the Bakers Admin API and 404 — so we always forward it when present.
 *
 * Triggers:
 *   - Profile page (my-profile): an element carrying `[data-active-coupons-trigger]`
 *     exists (rendered by `blocks/bakers-customer-profile.liquid`) → fetch on init.
 *   - Account page (my-account): the account coupons container
 *     `[data-cart-available-discounts][data-coupon-display-only]` (rendered by
 *     `blocks/_bakers-loyalty-card.liquid`) exists → fetch on init.
 *   - Cart drawer open: the `cart-drawer-component` (a `DialogComponent`)
 *     dispatches a non-bubbling `dialog:open` on itself; we catch it via a
 *     capture-phase listener on `document` (capture traverses to the target
 *     even for non-bubbling events).
 *
 * Endpoint:
 *   GET /apps/api/customer/active-coupons?goldenRecordId={mf08}&brand={baker|cobsca|cobsus}
 */

const ENDPOINT_BASE = '/apps/api/customer/active-coupons';

let inFlight = false;
let initialized = false;
// Set once a refresh completes successfully this page load. Profile init and a
// cart-drawer open share this flag so the two triggers stay aware of each other:
// the first rehydrate swaps *every* coupons container (the drawer's included) and
// MF17 is left fresh for any later SSR render, so subsequent triggers are no-ops
// (no second fetch, no second skeleton flash). Reset on full navigation only —
// the module re-initialises per page load.
let refreshed = false;

function readRootData() {
  const root = document.documentElement;
  const mf08 = (root.dataset.customerMf08 || '').trim();
  const brand = (root.dataset.hydrogenApiBrand || '').trim();
  return { mf08, brand };
}

/**
 * Toggles the skeleton-loader state on every rendered coupons container (cart
 * drawer / cart page / my-account). The container renders for linked customers
 * even with no coupons yet, so the skeletons have a mount point on a first
 * visit. CSS (`snippets/cart-available-discounts.liquid`) swaps the real list +
 * pagination for two skeleton cards while `data-coupons-loading="true"`.
 *
 * @param {boolean} isLoading
 */
function setCouponsLoading(isLoading) {
  const containers = document.querySelectorAll('[data-cart-available-discounts]');
  containers.forEach((container) => {
    if (isLoading) {
      container.setAttribute('data-coupons-loading', 'true');
    } else {
      container.removeAttribute('data-coupons-loading');
    }
  });
}

/**
 * Re-renders the coupon container(s) from a fresh server render after the
 * active-coupons fetch has written MF17. The cards are enriched server-side
 * (mo02 metaobject joined against MF17), so we can't rebuild them from the JSON
 * response — instead we re-fetch the current page and swap in the freshly
 * rendered `[data-cart-available-discounts]` markup, matched by context
 * (`data-coupon-display-only` = account vs cart). The swap drops the skeleton
 * state with it (fresh SSR markup carries no `data-coupons-loading`). Coupon
 * click handlers are delegated on `document`, so the replaced nodes need no
 * re-binding.
 *
 * The cart Coupons title's "(N available)" count (`snippets/cart-summary.liquid`)
 * lives outside the swapped container, so it's reconciled separately from the
 * same fresh document (text updated, or the span inserted/removed as the count
 * crosses zero). Best-effort: on any failure we leave the existing markup in
 * place (the `finally` in `refreshActiveCoupons` clears the skeleton either way).
 */
async function rehydrateCoupons() {
  const liveContainers = Array.from(document.querySelectorAll('[data-cart-available-discounts]'));
  const liveTitles = Array.from(document.querySelectorAll('.cart-offers__title'));
  if (liveContainers.length === 0 && liveTitles.length === 0) return;

  const res = await fetch(window.location.href, {
    headers: { Accept: 'text/html' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!res.ok) return;

  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const freshContainers = Array.from(doc.querySelectorAll('[data-cart-available-discounts]'));
  liveContainers.forEach((live) => {
    const displayOnly = live.hasAttribute('data-coupon-display-only');
    const fresh = freshContainers.find(
      (candidate) => candidate.hasAttribute('data-coupon-display-only') === displayOnly,
    );
    if (!fresh) return;
    // Strip the inline parse-time loading-init script before importing. A script
    // parsed by DOMParser never ran (so its "already started" flag is clear) and
    // WOULD execute on insertion into the live document — re-setting
    // `data-coupons-loading="true"` on the freshly-swapped real-coupon container
    // with no further fetch to clear it (stuck skeleton). The fresh markup also
    // carries no loading attribute, so the swap reveals the real cards.
    fresh.querySelectorAll('script[data-coupons-loading-init]').forEach((node) => node.remove());
    // Import into the live document before insertion (the parsed node belongs to
    // the throwaway DOMParser document); clone per match so two same-context
    // containers each get their own node.
    live.replaceWith(document.importNode(fresh, true));
  });

  // Reconcile the cart "(N available)" count. Matched by order (cart drawer /
  // cart page render the title in the same sequence as the fresh document).
  const freshTitles = doc.querySelectorAll('.cart-offers__title');
  liveTitles.forEach((liveTitle, index) => {
    const freshTitle = freshTitles[index];
    if (!freshTitle) return;
    const liveCount = liveTitle.querySelector('.cart-offers__count');
    const freshCount = freshTitle.querySelector('.cart-offers__count');
    if (freshCount) {
      if (liveCount) {
        liveCount.textContent = freshCount.textContent;
      } else {
        liveTitle.appendChild(document.importNode(freshCount, true));
      }
    } else if (liveCount) {
      liveCount.remove();
    }
  });
}

async function refreshActiveCoupons(triggerSource) {
  const { mf08, brand } = readRootData();

  // Guests / unlinked customers have no loyalty coupons to sync.
  if (!mf08) return;

  // Already refreshed this page load (e.g. profile init), so a later cart-drawer
  // open has nothing to do — the coupons are already live and MF17 is fresh.
  if (refreshed) return;

  // A coupon refresh is idempotent server-side, but there's no value in firing
  // overlapping requests (e.g. profile init + an immediate drawer open).
  if (inFlight) return;
  inFlight = true;
  setCouponsLoading(true);

  const params = new URLSearchParams({ goldenRecordId: mf08 });
  if (brand) params.set('brand', brand);
  const url = `${ENDPOINT_BASE}?${params.toString()}`;

  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    // 404 (no coupons record for this golden-record-id) and any other non-OK
    // status are non-fatal — just log for ops.
    if (!res.ok && res.status !== 404) {
      // eslint-disable-next-line no-console
      console.warn(`[customer-active-coupons] proxy ${res.status} (trigger=${triggerSource || 'unknown'})`);
    }

    // MF17 is now written (the route writes it before responding), so a fresh
    // server render reflects the current coupons. Swap them in so the cards
    // appear without a page reload. A 404 means "no coupons" — rehydrating still
    // reconciles the DOM (e.g. hides an empty container).
    await rehydrateCoupons();

    // Mark done so the other trigger (drawer open / profile init) skips its
    // redundant fetch. Only set on a clean run — a throw leaves it false so the
    // next trigger retries.
    refreshed = true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[customer-active-coupons] refresh failed', error);
  } finally {
    inFlight = false;
    setCouponsLoading(false);
  }
}

function handleDialogOpen(event) {
  const target = /** @type {Element | null} */ (event.target);
  if (target && typeof target.matches === 'function' && target.matches('cart-drawer-component')) {
    void refreshActiveCoupons('cart-drawer-open');
  }
}

/**
 * Pastes a clicked available-discount's code into the nearest cart Coupons
 * input. Delegated on `document` so it survives cart-drawer / cart-page section
 * morphs (the rendered blocks are replaced wholesale on cart updates).
 *
 * @param {Element} block - The `.cart-available-discount` element.
 */
/**
 * "Pasted" confirmation overlay on click. Re-adding the class after a reflow
 * restarts the CSS animation on rapid repeat clicks. The overlay reveals
 * instantly, holds ~1s, then fades out over ~1s (2s total — see the
 * `cart-coupon-pasted` keyframes).
 *
 * @param {Element} block - The `.cart-available-discount` element.
 */
function flashBlock(block) {
  block.classList.remove('is-flashing');
  // Force reflow so the animation restarts even if clicked again mid-flash.
  void (/** @type {HTMLElement} */ (block).offsetWidth);
  block.classList.add('is-flashing');
  const clear = () => block.classList.remove('is-flashing');
  block.addEventListener('animationend', clear, { once: true });
  // Fallback if animationend never fires; must outlast the 2s animation.
  setTimeout(clear, 2200);
}

function pasteCouponCode(block) {
  // Display-only contexts (e.g. the my-account list rendered with
  // `context: 'account'`) carry no cart Coupons input to paste into. Bail before
  // flashing or hunting for an input so a click is a no-op there.
  if (block.closest('[data-coupon-display-only]')) return;

  flashBlock(block);

  const code = (/** @type {HTMLElement} */ (block).dataset.couponCode || '').trim();
  if (!code) return;

  // Scope the input lookup to the same Coupons form the block lives in (a page
  // can have both a cart drawer and cart page form), falling back to the first
  // input anywhere if the block sits outside the usual structure.
  const scope =
    block.closest('.cart-discount__content') || block.closest('cart-discount-component') || document;
  const input = /** @type {HTMLInputElement | null} */ (
    scope.querySelector('.cart-discount__input') || document.querySelector('.cart-discount__input')
  );
  if (!input) return;

  input.value = code;
  // Notify any listeners (theme cart-discount component) the field changed.
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.focus();

  // Red outline on the pasted coupon block — only one at a time, so clear any
  // previously-outlined sibling first. Cleared by `handleCouponInput` when the
  // shopper empties the field.
  const group = block.closest('.cart-available-discounts');
  if (group) {
    group
      .querySelectorAll('.cart-available-discount.has-pasted-coupon')
      .forEach((el) => el.classList.remove('has-pasted-coupon'));
  }
  block.classList.add('has-pasted-coupon');
}

/**
 * Copies a display-only (account/profile) coupon's code to the clipboard and
 * flashes the "Copied" overlay. The profile page has no cart Coupons input to
 * paste into, so this mirrors the cart card's click interaction but writes the
 * code to the clipboard instead.
 *
 * @param {Element} block - The `.cart-available-discount` element.
 */
function copyCouponCode(block) {
  const code = (/** @type {HTMLElement} */ (block).dataset.couponCode || '').trim();
  if (!code) return;

  flashBlock(block);

  // Legacy fallback for browsers without the async Clipboard API (or when it's
  // blocked, e.g. non-secure contexts). The overlay still confirms visually.
  const fallbackCopy = () => {
    const textarea = document.createElement('textarea');
    textarea.value = code;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (error) {
      /* no-op: visual flash is the confirmation */
    }
    document.body.removeChild(textarea);
  };

  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    navigator.clipboard.writeText(code).catch(fallbackCopy);
  } else {
    fallbackCopy();
  }
}

/**
 * Removes the pasted-coupon outline once the Coupons field is emptied.
 * Delegated on `document` so it covers fields rendered after cart morphs.
 */
function handleCouponInput(event) {
  const target = /** @type {HTMLInputElement | null} */ (event.target);
  if (!target || typeof target.matches !== 'function') return;
  if (!target.matches('.cart-discount__input')) return;
  if (target.value.trim() !== '') return;

  const scope =
    target.closest('.cart-discount__content') || target.closest('cart-discount-component') || document;
  scope
    .querySelectorAll('.cart-available-discount.has-pasted-coupon')
    .forEach((el) => el.classList.remove('has-pasted-coupon'));
}

function handleCouponClick(event) {
  const target = /** @type {Element | null} */ (event.target);
  if (!target || typeof target.closest !== 'function') return;
  // The Terms & Conditions trigger has its own handler; a click on it must not
  // also copy/paste the card's code.
  if (target.closest('[data-coupon-terms-trigger]')) return;
  const block = target.closest('.cart-available-discount');
  if (!block) return;
  // Display-only (account/profile) cards copy to clipboard; cart cards paste
  // into the Coupons field.
  if (block.closest('[data-coupon-display-only]')) {
    copyCouponCode(block);
  } else {
    pasteCouponCode(block);
  }
}

function handleCouponKeydown(event) {
  if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
  const target = /** @type {Element | null} */ (event.target);
  if (!target || typeof target.closest !== 'function') return;
  // Let the Terms trigger handle its own Enter/Space activation.
  if (target.closest('[data-coupon-terms-trigger]')) return;
  const block = target.closest('.cart-available-discount');
  if (!block) return;
  // Space would otherwise scroll the page; Enter/Space "activate" the block.
  event.preventDefault();
  if (block.closest('[data-coupon-display-only]')) {
    copyCouponCode(block);
  } else {
    pasteCouponCode(block);
  }
}

/**
 * Shows only the current page's coupons (page size from the container) and
 * syncs the prev/next controls + status. Server renders page 1; this runs on
 * prev/next clicks.
 *
 * @param {Element} container - The `.cart-available-discounts` element.
 */
function renderDiscountPage(container) {
  const root = /** @type {HTMLElement} */ (container);
  const pageSize = parseInt(root.dataset.pageSize || '2', 10) || 2;
  const items = Array.from(container.querySelectorAll('.cart-available-discount'));
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  let page = parseInt(root.dataset.page || '1', 10) || 1;
  page = Math.min(Math.max(page, 1), totalPages);
  root.dataset.page = String(page);

  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  items.forEach((el, index) => {
    el.toggleAttribute('hidden', index < start || index >= end);
  });

  const status = container.querySelector('[data-discount-page-status]');
  if (status) status.textContent = `${page} / ${totalPages}`;
  const prev = container.querySelector('[data-discount-prev]');
  const next = container.querySelector('[data-discount-next]');
  if (prev) prev.toggleAttribute('disabled', page <= 1);
  if (next) next.toggleAttribute('disabled', page >= totalPages);
}

function handleDiscountPagination(event) {
  const target = /** @type {Element | null} */ (event.target);
  if (!target || typeof target.closest !== 'function') return;
  const control = target.closest('[data-discount-prev], [data-discount-next]');
  if (!control) return;
  const container = control.closest('.cart-available-discounts');
  if (!container) return;

  const root = /** @type {HTMLElement} */ (container);
  const current = parseInt(root.dataset.page || '1', 10) || 1;
  root.dataset.page = String(control.hasAttribute('data-discount-next') ? current + 1 : current - 1);
  renderDiscountPage(container);
}

/**
 * Closes a coupon Terms & Conditions dialog, tolerating browsers without the
 * native `<dialog>` API.
 *
 * @param {HTMLElement} dialog
 */
function closeTermsDialog(dialog) {
  if (typeof (/** @type {HTMLDialogElement} */ (dialog).close) === 'function') {
    /** @type {HTMLDialogElement} */ (dialog).close();
  } else {
    dialog.removeAttribute('open');
  }
}

/**
 * Opens the shared Terms & Conditions dialog (account/profile coupon list).
 * The clicked card carries a `<template data-coupon-terms>` holding the
 * rich-text HTML (metafield_tag output from the mo02 `terms_and_conditions`
 * field) that is injected into the dialog body. Backdrop click and the close
 * button dismiss it; Esc is handled natively by `<dialog>`.
 *
 * @param {Event} event
 */
function handleTermsDialog(event) {
  const target = /** @type {Element | null} */ (event.target);
  if (!target || typeof target.closest !== 'function') return;

  const trigger = target.closest('[data-coupon-terms-trigger]');
  if (trigger) {
    const container = trigger.closest('.cart-available-discounts') || document;
    const dialog = /** @type {HTMLElement | null} */ (
      container.querySelector('[data-coupon-terms-dialog]') ||
        document.querySelector('[data-coupon-terms-dialog]')
    );
    if (!dialog) return;

    const card = trigger.closest('.cart-available-discount');
    const template = /** @type {HTMLTemplateElement | null} */ (
      card && card.querySelector('[data-coupon-terms]')
    );
    const body = dialog.querySelector('[data-coupon-terms-body]');
    if (body) body.innerHTML = template ? template.innerHTML : '';

    if (typeof (/** @type {HTMLDialogElement} */ (dialog).showModal) === 'function') {
      /** @type {HTMLDialogElement} */ (dialog).showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    return;
  }

  const closeBtn = target.closest('[data-coupon-terms-close]');
  if (closeBtn) {
    const dialog = /** @type {HTMLElement | null} */ (closeBtn.closest('[data-coupon-terms-dialog]'));
    if (dialog) closeTermsDialog(dialog);
    return;
  }

  // Backdrop click: a click on the dialog element itself (not its inner card).
  if (typeof target.matches === 'function' && target.matches('[data-coupon-terms-dialog]')) {
    closeTermsDialog(/** @type {HTMLElement} */ (target));
  }
}

function init() {
  if (initialized) return;
  initialized = true;

  // Capture phase: `dialog:open` is dispatched non-bubbling on the
  // cart-drawer-component itself, but the capture phase still reaches it.
  document.addEventListener('dialog:open', handleDialogOpen, true);

  // Click / keyboard a rendered available-discount → paste its code into the
  // Coupons input. Delegated so it works across cart section re-renders.
  document.addEventListener('click', handleCouponClick);
  document.addEventListener('keydown', handleCouponKeydown);

  // Remove the pasted-coupon outline when the field is emptied.
  document.addEventListener('input', handleCouponInput);

  // Prev/next paging through the available-coupons list.
  document.addEventListener('click', handleDiscountPagination);

  // Account/profile coupons: open/close the Terms & Conditions dialog.
  document.addEventListener('click', handleTermsDialog);

  // On-load triggers. Either the customer-profile block (my-profile) or the
  // account coupons container rendered beneath the loyalty card (my-account) is
  // enough to warrant a refresh; `refreshActiveCoupons` dedupes overlapping
  // calls, so matching both is safe.
  if (
    document.querySelector('[data-active-coupons-trigger]') ||
    document.querySelector('[data-cart-available-discounts][data-coupon-display-only]')
  ) {
    void refreshActiveCoupons('account-page');
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
